import type { CeoInflowPlanRow } from '@shared/api';
import { moneyInput, parseMoney } from '@shared/lib/money';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { formatDateRu, formatMoney, INFLOW_STATUS_LABELS } from '../ceoFormat';
import { fullKs3Payment, type FactDraft, type FactField, type RowEditors } from './model';
import {
  ActionColumn,
  CollectionPctText,
  ContractNumberText,
  CustomerNameText,
  DeductionSubtext,
  DeductionText,
  DelayDaysText,
  DocNumberText,
  DueDateText,
  EditInputGroup,
  FieldError,
  FullCalcText,
  Input,
  ProjectNameText,
  ReceivableBalanceText,
  ReceivedAmountText,
  ResponsibleText,
  RowEditActions,
  RowError,
  Section,
  Table,
  TableWrap,
  Title,
} from './styles';

interface InflowsTableProps {
  rows: CeoInflowPlanRow[];
  /** Открытые редакторы факта — по id строки. */
  editors: RowEditors;
  onDraftChange: (rowId: string, patch: Partial<FactDraft>) => void;
  can_write: boolean;
  onStartEdit: (row: CeoInflowPlanRow) => void;
  onCancelEdit: (rowId: string) => void;
  onSaveRowFact: (row: CeoInflowPlanRow) => void;
}

export function InflowsTable({
  rows,
  editors,
  onDraftChange,
  can_write,
  onStartEdit,
  onCancelEdit,
  onSaveRowFact,
}: InflowsTableProps) {
  return (
    <Section>
      <Title>Пообъектная ведомость: План БДДС → Подписанные КС-3 → Фактически поступило</Title>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Строительный объект / Заказчик</th>
              <th>Договор / КС-3</th>
              <th>План БДДС</th>
              <th>Подписано КС-3 (брутто)</th>
              <th>Удержания (аванс + ГУ)</th>
              <th>Ожидается по КС-3 (нетто)</th>
              <th>Фактически поступило</th>
              <th>Остаток к получению</th>
              <th>Статус / Действие</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const editor = editors[r.id];
              const saving = Boolean(editor?.saving);
              const fieldId = (field: FactField) => `inflow-${r.id}-${field}`;
              const errorOf = (field: FactField) => editor?.fieldErrors[field];
              const full = editor
                ? fullKs3Payment(editor.draft.ks3, r.advance_deduction, r.retention_deduction, parseMoney)
                : null;
              return (
                <tr key={r.id}>
                  <td>
                    <ProjectNameText>{r.project_name}</ProjectNameText>
                    <CustomerNameText>{r.customer_name}</CustomerNameText>
                    {r.responsible_person && (
                      <ResponsibleText>{r.responsible_person}</ResponsibleText>
                    )}
                  </td>
                  <td>
                    <ContractNumberText>{r.contract_number}</ContractNumberText>
                    <DocNumberText>
                      {r.ks3_doc_number ?? 'Проект КС-3'}
                    </DocNumberText>
                    {r.expected_payment_date && (
                      <DueDateText>
                        Срок оплаты: {formatDateRu(r.expected_payment_date)}
                      </DueDateText>
                    )}
                  </td>
                  <td>{formatMoney(r.bdds_planned_amount)}</td>
                  <td>
                    {editor ? (
                      <EditInputGroup>
                        <Input
                          id={fieldId('ks3')}
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          aria-label={`Подписано по КС-3, ₽ — ${r.project_name}`}
                          value={editor.draft.ks3}
                          disabled={saving}
                          onChange={(e) => onDraftChange(r.id, { ks3: e.target.value })}
                          placeholder="Подписано по КС-3, ₽"
                          aria-invalid={errorOf('ks3') ? true : undefined}
                          aria-describedby={errorOf('ks3') ? `${fieldId('ks3')}-error` : undefined}
                        />
                        {errorOf('ks3') && (
                          <FieldError id={`${fieldId('ks3')}-error`}>{errorOf('ks3')}</FieldError>
                        )}
                      </EditInputGroup>
                    ) : r.ks3_signed_amount > 0 ? (
                      formatMoney(r.ks3_signed_amount)
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <DeductionText>
                      Аванс: {formatMoney(r.advance_deduction)}
                    </DeductionText>
                    <DeductionSubtext>
                      ГУ: {formatMoney(r.retention_deduction)}
                    </DeductionSubtext>
                  </td>
                  <td>
                    <strong>{formatMoney(r.expected_net_inflow)}</strong>
                  </td>
                  <td>
                    {editor ? (
                      <EditInputGroup>
                        <Input
                          id={fieldId('actual')}
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          aria-label={`Фактически поступило, ₽ — ${r.project_name}`}
                          value={editor.draft.actual}
                          disabled={saving}
                          onChange={(e) => onDraftChange(r.id, { actual: e.target.value })}
                          placeholder="Факт прихода, ₽"
                          aria-invalid={errorOf('actual') ? true : undefined}
                          aria-describedby={
                            errorOf('actual') ? `${fieldId('actual')}-error` : undefined
                          }
                        />
                        {errorOf('actual') && (
                          <FieldError id={`${fieldId('actual')}-error`}>
                            {errorOf('actual')}
                          </FieldError>
                        )}
                        <Input
                          id={fieldId('ks3DocNo')}
                          type="text"
                          aria-label={`Номер справки КС-3 — ${r.project_name}`}
                          value={editor.draft.ks3DocNo}
                          disabled={saving}
                          onChange={(e) => onDraftChange(r.id, { ks3DocNo: e.target.value })}
                          placeholder="№ КС-3"
                        />
                        {full && full.net > 0 && (
                          <FullCalcText>
                            100 % КС-3: {formatMoney(full.ks3)} − аванс{' '}
                            {formatMoney(r.advance_deduction)} − ГУ{' '}
                            {formatMoney(r.retention_deduction)} = {formatMoney(full.net)}
                          </FullCalcText>
                        )}
                      </EditInputGroup>
                    ) : (
                      <>
                        <ReceivedAmountText $hasReceived={r.actual_received_amount > 0}>
                          {formatMoney(r.actual_received_amount)}
                        </ReceivedAmountText>
                        <CollectionPctText>
                          {r.ks3_collection_pct.toFixed(1)}% от КС-3
                        </CollectionPctText>
                      </>
                    )}
                  </td>
                  <td>
                    <ReceivableBalanceText $hasBalance={r.ks3_receivable_balance > 0}>
                      {formatMoney(r.ks3_receivable_balance)}
                    </ReceivableBalanceText>
                    {r.delay_days > 0 && (
                      <DelayDaysText>
                        просрочка {r.delay_days} дн.
                      </DelayDaysText>
                    )}
                  </td>
                  <td>
                    <ActionColumn>
                      <Badge
                        $tone={
                          r.status === 'paid_in_full'
                            ? 'success'
                            : r.status === 'overdue_receivable'
                              ? 'danger'
                              : r.status === 'partially_paid'
                                ? 'info'
                                : 'warning'
                        }
                      >
                        {INFLOW_STATUS_LABELS[r.status] ?? r.status}
                      </Badge>
                      {can_write &&
                        (editor ? (
                          <RowEditActions>
                            <Button
                              $size="sm"
                              type="button"
                              disabled={saving}
                              onClick={() => onSaveRowFact(r)}
                            >
                              {saving ? 'Сохраняю…' : 'Сохранить'}
                            </Button>
                            <Button
                              $size="sm"
                              $variant="secondary"
                              type="button"
                              disabled={saving || !full || full.net <= 0}
                              onClick={() =>
                                full && onDraftChange(r.id, { actual: moneyInput(full.net) })
                              }
                              title="Подставить в «Фактически поступило» сумму КС-3 из редактора за вычетом аванса и ГУ"
                            >
                              100% КС-3
                            </Button>
                            <Button
                              $size="sm"
                              $variant="ghost"
                              type="button"
                              disabled={saving}
                              onClick={() => onCancelEdit(r.id)}
                              aria-label="Отменить правку строки"
                            >
                              ✕
                            </Button>
                          </RowEditActions>
                        ) : (
                          <Button
                            $size="sm"
                            $variant="ghost"
                            onClick={() => onStartEdit(r)}
                          >
                            Отметить приход / КС-3
                          </Button>
                        ))}
                      {editor?.error && <RowError role="alert">{editor.error}</RowError>}
                    </ActionColumn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
    </Section>
  );
}
